import type { ReactElement, ReactNode } from 'react';
import React, { useEffect } from 'react';
import classNames from '../../utils/classNames';
import { branch$, siteTitle$ } from './title-state';

function generateTitle(pageTitle: string): string {
    return `${branch$.getValue()}: ${pageTitle} | ${siteTitle$.getValue()}`;
}

export interface BasePageProps {
    // Name of the page. Used as the heading and as the browser tab title.
    title: string;
    // Custom heading content, rendered instead of `title`. The browser tab keeps using `title`.
    titleNode?: ReactElement;
    besideTitle?: string | ReactElement;
    beta?: boolean;
    children: ReactNode;
    className?: string;
    wrapMainContainer?: boolean;
    forModalUsage?: boolean;
}

function InnerPageWrapper({ className, children }: { className?: string; children: ReactNode }) {
    if (className) {
        return <div className={className}>{children}</div>;
    }

    return <>{children}</>;
}

export function BasePage(props: BasePageProps) {
    const { wrapMainContainer = true, forModalUsage = false } = props;
    useEffect(() => {
        // Change the page title when this component mounts.
        const titleElement = document.querySelector('title');
        if (titleElement) {
            titleElement.text = generateTitle(props.title);
        }
    }, [props.title]);

    return (
        <>
            <div className={classNames(!forModalUsage && 'main-container-new pt-5', 'flex pb-4')}>
                <h1 className="after:border-primary my-0 grow text-2xl font-light leading-[26px] after:mt-[22px] after:block after:w-[60px] after:border-b-4">
                    {props.titleNode ?? props.title}
                    {props.beta && (
                        <span className="ml-3 after:rounded after:border after:border-black after:px-1 after:py-0.5 after:align-middle after:text-[9px] after:uppercase after:content-['beta']"></span>
                    )}
                </h1>
                {props.besideTitle && <div className="flex-none">{props.besideTitle}</div>}
            </div>
            <InnerPageWrapper
                className={classNames(
                    wrapMainContainer && 'main-container-new pb-4',
                    props.className
                )}
            >
                {props.children}
            </InnerPageWrapper>
        </>
    );
}
